import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Text, Box, HStack, Icon, SimpleGrid, useToast, Badge,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure, Spinner,
} from '@chakra-ui/react';
import { FiUpload, FiTrash2 } from 'react-icons/fi';
import { alumbradoApi, UPLOADS_BASE, SmArchivo } from '../../../api/serviciosApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  reporteId: number;
  titulo: string;
  onGuardado: () => void;
}

const ZonaMomento: React.FC<{
  momento: 'ANTES' | 'DESPUES';
  archivos: SmArchivo[];
  subiendo: boolean;
  onUpload: (file: File, momento: 'ANTES' | 'DESPUES') => void;
  onRemove: (a: SmArchivo) => void;
}> = ({ momento, archivos, subiendo, onUpload, onRemove }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Box borderWidth="1px" borderRadius="md" p={3}>
      <HStack justify="space-between" mb={2}>
        <Badge colorScheme={momento === 'ANTES' ? 'orange' : 'green'} px={2} py={1}>{momento}</Badge>
        <Text fontSize="xs" color="gray.500">{archivos.length} foto(s)</Text>
      </HStack>
      <HStack spacing={2} flexWrap="wrap" mb={3}>
        {archivos.length === 0 && <Text fontSize="xs" color="gray.400">Sin fotografías</Text>}
        {archivos.map((a) => (
          <Box key={a.id} position="relative" w="80px" h="80px">
            <Box as="a" href={`${UPLOADS_BASE}${a.rutaArchivo}`} target="_blank"
              w="80px" h="80px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200" display="block">
              <img src={`${UPLOADS_BASE}${a.rutaArchivo}`} alt={a.nombreArchivo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </Box>
            <Button size="xs" position="absolute" top="2px" right="2px" colorScheme="red"
              borderRadius="full" p={0} minW="18px" h="18px" onClick={() => onRemove(a)}>
              <Icon as={FiTrash2} boxSize={2.5} />
            </Button>
          </Box>
        ))}
      </HStack>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f, momento); e.target.value = ''; }} />
      <Button size="sm" leftIcon={subiendo ? <Spinner size="xs" /> : <Icon as={FiUpload} />}
        variant="outline" colorScheme={momento === 'ANTES' ? 'orange' : 'green'}
        onClick={() => inputRef.current?.click()} isDisabled={subiendo}>
        Agregar foto {momento}
      </Button>
    </Box>
  );
};

const ModalAlumbradoEvidencias: React.FC<Props> = ({ isOpen, onClose, reporteId, titulo, onGuardado }) => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [archivos, setArchivos] = useState<SmArchivo[]>([]);
  const [cargando, setCargando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [archivoAEliminar, setArchivoAEliminar] = useState<SmArchivo | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const fetchDetalle = async () => {
    setCargando(true);
    try {
      const res = await alumbradoApi.get(reporteId);
      setArchivos((res?.archivos || []).filter((a: any) => a.tipoArchivo === 'foto' || !a.tipoArchivo));
    } catch {
      setArchivos([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { if (isOpen) fetchDetalle(); /* eslint-disable-next-line */ }, [isOpen, reporteId]);

  const handleUpload = async (file: File, momento: 'ANTES' | 'DESPUES') => {
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('momento', momento);
      fd.append('tipoArchivo', 'foto');
      await alumbradoApi.addArchivo(reporteId, fd);
      toast({ title: `Foto ${momento} subida`, status: 'success', duration: 2000 });
      await fetchDetalle();
      onGuardado();
    } catch (error: any) {
      toast({ title: 'Error al subir', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally {
      setSubiendo(false);
    }
  };

  const handleRemoveClick = (a: SmArchivo) => { setArchivoAEliminar(a); onOpenEliminar(); };

  const handleConfirmarEliminar = async () => {
    if (!archivoAEliminar) return;
    setEliminando(true);
    try {
      await alumbradoApi.removeArchivo(reporteId, archivoAEliminar.id);
      toast({ title: 'Archivo eliminado', status: 'success', duration: 2000 });
      onCloseEliminar();
      await fetchDetalle();
      onGuardado();
    } catch (error: any) {
      toast({ title: 'Error al eliminar', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally {
      setEliminando(false);
    }
  };

  const antes = archivos.filter((a) => a.momento === 'ANTES');
  const despues = archivos.filter((a) => a.momento === 'DESPUES');

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Evidencias fotográficas — {titulo}
          </ModalHeader>
          <ModalBody py={5}>
            {cargando ? (
              <Box textAlign="center" py={8}><Spinner color="#5b182b" /></Box>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <ZonaMomento momento="ANTES" archivos={antes} subiendo={subiendo} onUpload={handleUpload} onRemove={handleRemoveClick} />
                <ZonaMomento momento="DESPUES" archivos={despues} subiendo={subiendo} onUpload={handleUpload} onRemove={handleRemoveClick} />
              </SimpleGrid>
            )}
          </ModalBody>
          <ModalFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color="#5b182b">Eliminar archivo</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar <strong>{archivoAEliminar?.nombreArchivo}</strong>? Esta acción no se puede deshacer.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={eliminando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleConfirmarEliminar} ml={3} isLoading={eliminando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ModalAlumbradoEvidencias;
